// src/utils/activityReports.ts
// Helpers purs pour la logique métier des rapports d'activité (testables)

import dayjs from 'dayjs';
import { ActivityReport, ValidationMode } from '../types/activity';
import { ActivityReportConfiguration } from '../types/business';
import {
  ActivityReportDeadlineOptions,
  WeeklyDeadlineDay,
  clampDeadlineHour,
  DEFAULT_ACTIVITY_REPORT_DEADLINE_HOUR,
  getActivityReportValidationDeadline,
  isActivityReportValidatedLate,
} from './dateHelpers';
import { createI18nLabelMap } from './i18n/getStatusLabel';

export const WEEKLY_DEADLINE_DAY_LABELS: Record<WeeklyDeadlineDay, string> = createI18nLabelMap(
  'weeklyDeadlineDay',
  ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const
);

export const WEEKLY_DEADLINE_DAY_OPTIONS: WeeklyDeadlineDay[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export const formatDeadlineHourLabel = (hour: number | null | undefined): string => {
  const h = clampDeadlineHour(hour);
  return `${String(h).padStart(2, '0')}h00`;
};

export const DEADLINE_HOUR_OPTIONS: number[] = Array.from({ length: 24 }, (_, i) => i);

const isWeeklyDeadlineDay = (value: unknown): value is WeeklyDeadlineDay =>
  typeof value === 'string' && value in WEEKLY_DEADLINE_DAY_LABELS;

/**
 * Normalise la config employé avant persistance / usage runtime.
 */
export const sanitizeEmployeeActivityReportConfig = (
  config:
    | {
        validationMode?: ValidationMode | null;
        weeklyDeadlineDay?: WeeklyDeadlineDay | string | null;
        deadlineHour?: number | null;
      }
    | null
    | undefined
): ActivityReportConfiguration | null => {
  if (!config?.validationMode) return null;

  const deadlineHour = clampDeadlineHour(config.deadlineHour);
  const result: ActivityReportConfiguration = {
    validationMode: config.validationMode,
    deadlineHour,
  };

  if (config.validationMode === 'weekly') {
    result.weeklyDeadlineDay = isWeeklyDeadlineDay(config.weeklyDeadlineDay)
      ? config.weeklyDeadlineDay
      : 'sunday';
  }

  return result;
};

/**
 * Snapshot deadline à la création du rapport (évite le drift si la config employé change).
 */
export const buildActivityReportDeadlineFields = (
  periodStart: Date,
  config: ActivityReportConfiguration
): {
  validationDeadlineAt: Date;
  deadlineHour: number;
  weeklyDeadlineDay?: WeeklyDeadlineDay;
} => {
  const deadlineHour = clampDeadlineHour(config.deadlineHour);
  const weeklyDeadlineDay =
    config.validationMode === 'weekly' ? (config.weeklyDeadlineDay ?? 'sunday') : undefined;
  const options: ActivityReportDeadlineOptions = {
    weeklyDeadlineDay,
    deadlineHour,
  };

  return {
    validationDeadlineAt: getActivityReportValidationDeadline(
      periodStart,
      config.validationMode,
      options
    ),
    deadlineHour,
    ...(weeklyDeadlineDay ? { weeklyDeadlineDay } : {}),
  };
};

/**
 * Résout la deadline d'un rapport : snapshot prioritaire, sinon recalcul legacy.
 */
export const resolveActivityReportDeadline = (
  report: ActivityReport,
  config: ActivityReportConfiguration | null
): Date | null => {
  if (report.validationDeadlineAt) {
    return report.validationDeadlineAt;
  }

  const mode: ValidationMode = report.validationMode ?? config?.validationMode ?? 'daily';
  const weeklyDeadlineDay =
    report.weeklyDeadlineDay ?? config?.weeklyDeadlineDay ?? 'sunday';
  const deadlineHour =
    report.deadlineHour ?? config?.deadlineHour ?? DEFAULT_ACTIVITY_REPORT_DEADLINE_HOUR;
  const referenceDate = report.periodStart ?? report.date;

  return getActivityReportValidationDeadline(referenceDate, mode, {
    weeklyDeadlineDay,
    deadlineHour,
  });
};

/**
 * Vérifie si un rapport validé a été validé en retard (validatedAt > deadline).
 */
export const isLate = (
  report: ActivityReport,
  config: ActivityReportConfiguration | null
): boolean => {
  if (!report.validatedAt) return false;
  const deadline = resolveActivityReportDeadline(report, config);
  if (!deadline) return false;
  return new Date(report.validatedAt).getTime() > deadline.getTime();
};

/**
 * Vérifie si un rapport brouillon est en retard (deadline dépassée).
 */
export const isOverdueDraft = (
  report: ActivityReport,
  config: ActivityReportConfiguration | null
): boolean => {
  if (report.status !== 'draft') return false;
  const deadline = resolveActivityReportDeadline(report, config);
  if (!deadline) return false;
  return Date.now() > deadline.getTime();
};

/**
 * Formate le libellé de période d'un rapport pour l'affichage.
 */
export const formatReportPeriodLabel = (report: ActivityReport): string => {
  if (report.validationMode === 'weekly' && report.periodStart) {
    const periodEnd = report.periodEnd ?? dayjs(report.periodStart).endOf('isoWeek').toDate();
    return `Semaine du ${dayjs(report.periodStart).format('DD MMM')} au ${dayjs(periodEnd).format('DD MMM YYYY')}`;
  }

  if (report.validationMode === 'monthly' && report.periodStart) {
    return dayjs(report.periodStart).format('[Mois de] MMMM YYYY');
  }

  return dayjs(report.date).format('dddd DD MMMM YYYY');
};

/**
 * Message d'info sur la deadline selon la config employé.
 */
export const getValidationDeadlineMessage = (
  validationMode: ValidationMode,
  weeklyDeadlineDayOrOptions?:
    | WeeklyDeadlineDay
    | 'friday'
    | 'saturday'
    | 'sunday'
    | ActivityReportDeadlineOptions,
  maybeDeadlineHour?: number
): string => {
  let weeklyDeadlineDay: WeeklyDeadlineDay = 'sunday';
  let deadlineHour = DEFAULT_ACTIVITY_REPORT_DEADLINE_HOUR;

  if (typeof weeklyDeadlineDayOrOptions === 'string') {
    weeklyDeadlineDay = isWeeklyDeadlineDay(weeklyDeadlineDayOrOptions)
      ? weeklyDeadlineDayOrOptions
      : 'sunday';
    deadlineHour = clampDeadlineHour(maybeDeadlineHour);
  } else if (weeklyDeadlineDayOrOptions && typeof weeklyDeadlineDayOrOptions === 'object') {
    weeklyDeadlineDay = weeklyDeadlineDayOrOptions.weeklyDeadlineDay ?? 'sunday';
    deadlineHour = clampDeadlineHour(weeklyDeadlineDayOrOptions.deadlineHour);
  }

  const hourLabel = formatDeadlineHourLabel(deadlineHour);

  if (validationMode === 'daily') {
    return `À valider le jour du rapport avant ${hourLabel}.`;
  }

  if (validationMode === 'weekly') {
    const dayLabel = WEEKLY_DEADLINE_DAY_LABELS[weeklyDeadlineDay]?.toLowerCase() ?? 'dimanche';
    return `À valider avant le ${dayLabel} à ${hourLabel}.`;
  }

  if (validationMode === 'monthly') {
    return `À valider le dernier jour du mois avant ${hourLabel}.`;
  }

  return '';
};

/** @deprecated Prefer resolveActivityReportDeadline; kept for callers that used low-level late helper */
export { isActivityReportValidatedLate };
